<script setup lang="ts">
import { useSpaceSites } from "@/api/site-query";
import { useAddSiteToSpace, useRemoveSiteFromSpace } from "@/api/space-query";
import ConfigSection from "@/layouts/content/ConfigSection.vue";
import { useSnackbarStore } from "@/stores";
import { useQueryManager } from "@/stores/query-manager";
import { useScopeStore } from "@/stores/scope.ts";
import { mdiLinkOff } from "@mdi/js";
import { Site, StationSummary } from "@soft-db/monitoring-api-client";
import { Button, confirm, Icon, makeColumnHelper, Tooltip } from "@soft-db/ui";
import AppDataTable from "../common/AppDataTable.vue";
import ColumnName from "../common/ColumnName.vue";
import PermissionGuard from "../permission/PermissionGuard.vue";
import StationLinkedBadge from "../stations/StationLinkedBadge.vue";
import NavBarStatus from "../workspace/NavBarStatus.vue";
import AddSiteDialog from "./AddSiteDialog.vue";

type SpaceSitesRowData = {
	id: string;
	name: string;
	description: string;
	station: StationSummary | null;
};

const props = defineProps({ id: { type: String, required: true } });
const id = computed(() => props.id);

const { t } = useI18n();

const snackbar = useSnackbarStore();

const { distributorScope: scope } = useScopeStore();

const { sites } = useSpaceSites(id);
const addSpaceSite = useAddSiteToSpace();
const removeSpaceSite = useRemoveSiteFromSpace();

const siteRows = computed<SpaceSitesRowData[]>(() => {
	if (!sites.value) return [];

	return sites.value.map((s) => ({
		id: s.id,
		name: s.name,
		description: s.description,
		station: s.station,
	}));
});

const { Column } = makeColumnHelper<SpaceSitesRowData>();

const addSiteDialog = ref(false);
function openAddSiteDialog() {
	addSiteDialog.value = true;
}

const queryManager = useQueryManager();

async function addSites(sites: Site[]) {
	await queryManager.chainInvalidation(async () => {
		for (const site of sites) {
			await addSpaceSite.mutateAsync({ space: id.value, site: site.id, owner: false });
		}
	});
	snackbar.openOnSaved();
}

async function removeSite(siteId: string, name: string) {
	if (await confirm.yesNo(t("space.removeObjectConfirmation", { n: name }))) {
		await removeSpaceSite.mutateAsync({ space: id.value, site: siteId });
		snackbar.openOnSaved();
	}
}
</script>

<template>
	<ConfigSection :title="t('object.project', 2)" :description="t('space.sitesDescription')">
		<AppDataTable layout="contained" :data="siteRows" :pagination="{ pageIndex: 0, pageSize: 10 }">
			<template #action>
				<Button variant="outline" @click="openAddSiteDialog">{{ t("space.addSite") }}</Button>
			</template>
			<template #columns>
				<Column
					v-slot="{ row: { original: site } }"
					field="name"
					:header="t('common.name')"
					enableSorting
					enableGlobalFilter
				>
					<ColumnName :name="site.name" :description="site.description" />
				</Column>

				<Column
					id="station"
					v-slot="{ row: { original: site } }"
					:fieldFn="(d) => d.station?.name || ''"
					:header="t('object.station', 1)"
					enableSorting
					enableGlobalFilter
				>
					<StationLinkedBadge :station="site.station" />
				</Column>

				<Column v-slot="{ row: { original: site } }" shrink>
					<div class="flex items-center gap-1">
						<NavBarStatus class="mr-4" sourceType="site" :sourceId="site.id" />
						<PermissionGuard permission="space.site.delete">
							<Tooltip :tooltip="t('space.unlinkSite')">
								<Button
									class="float-right"
									variant="soft"
									color="error"
									size="sm"
									@click="removeSite(site.id, site.name)"
								>
									<Icon :d="mdiLinkOff" />
								</Button>
							</Tooltip>
						</PermissionGuard>
					</div>
				</Column>
			</template>
		</AppDataTable>
	</ConfigSection>

	<AddSiteDialog
		v-model:dialog="addSiteDialog"
		:scope="scope"
		:ignores="sites?.map((s) => s.id) || []"
		@select="addSites"
	/>
</template>
